import { applyMenuIcon, backPaw, el, pictureButton } from './dom.js';
import type { MenuIconId } from '../content/menu-icons.js';

/**
 * The layer under every menu, so a screen file only says what is *different* about it.
 *
 * Three zones, and the grid owns all three:
 *
 * ```
 * +----------------------------------------------+
 * | [paw]                             (badge)    |  reserved bar
 * +----------------------------------------------+
 * |                  content                     |  fills, never scrolls
 * +----------------------------------------------+
 * | (page turn, only where a screen pages)       |
 * +----------------------------------------------+
 * ```
 *
 * **The bar is reserved rather than padded around.** Every screen used to hold its own space for the
 * back paw with a top padding of `min(var(--tap-min), 22vmin) + 8px`, copied from the last screen that
 * needed it - three copies of one number, and the paw still ends up over the content when one of them
 * drifts. Here the paw's corner is a grid track that nothing else can occupy.
 *
 * **The badge is the only title a pre-reader can have.** A picture on the right of the bar saying which
 * screen this is: the Garage's garage, the Collection's treat jar, the chooser's crayon. It is not a
 * control and never takes a press.
 *
 * **Nothing scrolls.** A shelf she has to scroll is a shelf whose far end she never sees - she is three,
 * and a flick that moves a grid is not a gesture she has. Screens with more than a screenful page
 * instead: one big button in the bottom slot, and a row of pips saying how far along she is. The slot is
 * empty, and takes no room, on a screen that fits.
 */

export interface MenuShell {
  /** The screen element, ready to be appended. */
  readonly screen: HTMLElement;
  /** Where the screen puts its own content. Fills the space between bar and foot. */
  readonly content: HTMLElement;
  /**
   * Lay cells out `perPage` at a time, and wire the page turn.
   *
   * The cells are built once and moved between pages rather than rebuilt on every turn: a garage card
   * carries a thumbnail loaded from IndexedDB, and rebuilding it would re-fetch the picture and flash
   * the card every time she looked at the next page.
   */
  readonly paginate: (cells: readonly HTMLElement[], perPage: number) => void;
}

export interface MenuShellOptions {
  /** The screen's own class, e.g. `screen--garage`. It gets `screen` and `screen--menu` too. */
  readonly screenClass: string;
  /** The picture that says which screen this is. */
  readonly badge: MenuIconId;
  readonly onBack: () => void;
  /** Parent-facing only, on the badge. */
  readonly badgeTitle?: string;
}

export function menuShell(options: MenuShellOptions): MenuShell {
  const badge = el('div', {
    className: 'menu-badge',
    attrs: { 'aria-hidden': 'true' },
  });
  if (options.badgeTitle !== undefined) badge.title = options.badgeTitle;
  applyMenuIcon(badge, options.badge);

  const content = el('div', { className: 'menu-content' });
  const foot = el('div', { className: 'menu-foot' });
  const bar = el('div', { className: 'menu-bar', children: [badge] });

  const screen = el('div', {
    className: `screen screen--menu ${options.screenClass}`,
    children: [
      /*
       * The paw is a child of the screen rather than of the bar, and stays absolutely positioned in the
       * corner: its stacking guarantee is the one thing on these screens that is held by a test, and it
       * is the same paw in the same place on screens that have no shell at all. The bar's first track is
       * what keeps anything else out from under it.
       */
      backPaw(options.onBack),
      bar,
      content,
      foot,
    ],
  });

  /*
   * Which page she is on, kept out here rather than per call.
   *
   * `paginate` runs again whenever the set of cells changes - throwing a car away is the case - and
   * being sent back to the front of the shelf because one card went is a screen moving under her hands.
   */
  let current = 0;

  return {
    screen,
    content,
    paginate(cells, perPage) {
      const pages = Math.max(1, Math.ceil(cells.length / perPage));

      const grid = el('div', { className: 'menu-grid' });
      content.replaceChildren(grid);

      const show = (page: number): void => {
        current = ((page % pages) + pages) % pages;
        grid.replaceChildren(...cells.slice(current * perPage, (current + 1) * perPage));
        for (const [index, pip] of pips.entries()) {
          pip.classList.toggle('is-current', index === current);
        }
      };

      // One pip per page, filled for the one she is on. Not a number - she cannot read one - and the
      // same shape the drive ribbon uses for "how many things, how far along".
      const pips = Array.from({ length: pages }, () => el('span', { className: 'menu-pip' }));

      if (pages > 1) {
        /*
         * One button, and it wraps. Two buttons means one of them is dead on the first page and the
         * other on the last, and "the button that did something a moment ago now does nothing" is the
         * worst thing a control can do to someone who cannot read the reason. Going round is also how
         * she gets back to the front without a second idea to learn.
         */
        const turn = pictureButton([], () => show(current + 1), {
          className: 'menu-turn card card--pressable',
          title: 'next page',
          icon: 'arrow',
        });
        foot.replaceChildren(el('div', { className: 'menu-pips', children: pips }), turn);
      } else {
        foot.replaceChildren();
      }

      // Clamped, not reset: the last page can have just lost its only card.
      show(Math.min(current, pages - 1));
    },
  };
}
